import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Chip,
  Box,
  Avatar,
} from "@mui/material";
import { memo, useMemo, useState } from "react";
import { IOperatorPerformance } from "../../../interfaces/dashboard";
import {
  formatNumber,
  formatRate,
  formatDays,
  formatCurrency,
  formatCarrierName,
} from "../../../utils/metricsFormatters";
import {
  getSlaComplianceSeverity,
  getDeliveryRateSeverity,
  SEVERITY_COLOR,
} from "../../../utils/MetricThresholds";
import { LOCALE } from "../../../utils/constants";

interface IOperatorsPerformanceProps {
  data: IOperatorPerformance[];
}

type TSortKey = keyof IOperatorPerformance;
type TSortDir = "asc" | "desc";

const numericCompare = (av: string | number, bv: string | number): number => {
  if (av < bv) return -1;
  if (av > bv) return 1;
  return 0;
};

const compareValues = (av: string | number, bv: string | number): number =>
  typeof av === "string" && typeof bv === "string"
    ? av.localeCompare(bv, LOCALE)
    : numericCompare(av, bv);

const compareFn = (
  av: string | number | null,
  bv: string | number | null,
  sortAsc: boolean,
): number => {
  // Los valores nulos van siempre al fondo, sin importar la dirección.
  if (av === null || av === undefined) return 1;
  if (bv === null || bv === undefined) return -1;
  const cmp = compareValues(av, bv);
  return sortAsc ? cmp : -cmp;
};

const sortDir = (isActive: boolean, sortAsc: boolean): TSortDir => {
  if (isActive && sortAsc) return "asc";
  return "desc";
};

const initialsOf = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

const RateBadge = ({
  rate,
  getSeverity,
}: {
  rate: number;
  getSeverity: (_n: number) => string;
}) => (
  <Chip
    label={formatRate(rate)}
    size="small"
    sx={{
      backgroundColor: "transparent",
      color:
        SEVERITY_COLOR[
          getSeverity(rate) as "success" | "warning" | "error" | "info"
        ],
      fontWeight: 600,
    }}
  />
);

const OperatorsPerformance = ({ data }: IOperatorsPerformanceProps) => {
  const [sortKey, setSortKey] = useState<TSortKey>("totalOrders");
  const [sortAsc, setSortAsc] = useState(false);

  const handleSort = (key: TSortKey) => {
    if (key === sortKey) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  // Memoizado: la copia + sort corre en cada render; solo depende de los datos
  // y del criterio de orden.
  const sorted = useMemo(
    () =>
      [...data].sort((a, b) =>
        compareFn(
          a[sortKey] as string | number | null,
          b[sortKey] as string | number | null,
          sortAsc,
        ),
      ),
    [data, sortKey, sortAsc],
  );

  const col = (key: TSortKey, label: string) => {
    const active = sortKey === key;
    return (
      <TableCell sortDirection={active ? sortDir(active, sortAsc) : false}>
        <TableSortLabel
          active={active}
          direction={sortDir(active, sortAsc)}
          onClick={() => handleSort(key)}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    );
  };

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            {col("carrier_name", "Operador")}
            {col("totalOrders", "Envíos")}
            {/* Bloque de resultado de entrega: Éxito (entregados) junto a
                Cancelaciones y Devoluciones. El SLA va aparte, al final. */}
            {col("deliveredRate", "% Éxito")}
            {/*
              TODO(backend): "% Cancelaciones" y "% Devoluciones" por operador
              (Figma) no están en IOperatorPerformance (canceledRate /
              returnedRate a nivel operador no existen hoy, solo a nivel
              dashboard general). Columnas dejadas listas con placeholder,
              sin ordenamiento, hasta que el backend las exponga.
            */}
            <TableCell align="right">% Cancelaciones</TableCell>
            <TableCell align="right">% Devoluciones</TableCell>
            {col("avgDeliveryDays", "Tiempo prom.")}
            {col("avgCost", "Costo prom.")}
            {/* SLA aparte del bloque de entrega. Cumplimiento = complemento del
                % de SLA roto (B2); se ordena por slaBreachedRate (asc = mayor
                cumplimiento primero). */}
            {col("slaBreachedRate", "% Cumpl. SLA")}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row, index) => (
            <TableRow key={row.carrier_id} hover>
              <TableCell>{index + 1}</TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                    {initialsOf(formatCarrierName(row.carrier_name, row.carrier_id))}
                  </Avatar>
                  {formatCarrierName(row.carrier_name, row.carrier_id)}
                </Box>
              </TableCell>
              <TableCell>{formatNumber(row.totalOrders)}</TableCell>
              <TableCell>
                <RateBadge
                  rate={row.deliveredRate}
                  getSeverity={getDeliveryRateSeverity}
                />
              </TableCell>
              <TableCell align="right">—</TableCell>
              <TableCell align="right">—</TableCell>
              <TableCell>{formatDays(row.avgDeliveryDays)}</TableCell>
              <TableCell>
                {row.avgCost === null ? "—" : formatCurrency(row.avgCost)}
              </TableCell>
              <TableCell>
                <RateBadge
                  rate={100 - row.slaBreachedRate}
                  getSeverity={getSlaComplianceSeverity}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default memo(OperatorsPerformance);
